import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  numberAttribute,
} from '@angular/core';

export type DsDotBulletState = 'default' | 'hover' | 'focus' | 'selected' | 'in-progress';
export type DsDotBulletStyle = 'outlined' | 'filled';

@Component({
  selector: 'ds-dot-bullet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      #button
      type="button"
      class="ds-dot-bullet"
      [class.ds-dot-bullet--hover]="state === 'hover'"
      [class.ds-dot-bullet--focus]="state === 'focus'"
      [class.ds-dot-bullet--selected]="state === 'selected'"
      [class.ds-dot-bullet--in-progress]="state === 'in-progress'"
      [class.ds-dot-bullet--progress-static]="!progressAnimationEnabled"
      [class.ds-dot-bullet--progress-paused]="progressPaused"
      [class.ds-dot-bullet--invert]="invert"
      [class.ds-dot-bullet--filled]="style === 'filled'"
      [style.--_progress-duration]="progressDurationCss"
      [style.--_progress-start-scale]="progressStartScale"
      [disabled]="disabled || null"
      [attr.aria-current]="ariaCurrent ? 'true' : null"
      [attr.aria-label]="ariaLabel || null"
      [attr.tabindex]="tabIndex"
      (click)="handleClick()"
    >
      @if (state === 'in-progress') {
        <span class="ds-dot-bullet__progress-track" aria-hidden="true">
          <span #progressIndicator class="ds-dot-bullet__progress-indicator"></span>
        </span>
      } @else {
        <span class="ds-dot-bullet__dot" aria-hidden="true"></span>
      }
    </button>
  `,
  styles: [`
    :host {
      display: inline-flex;
      flex: 0 0 auto;
    }

    .ds-dot-bullet {
      --_default-color: var(--ds-dot-bullet-outlined-color);
      --_selected-color: var(--ds-dot-bullet-filled-selected-color);
      --_hover-background: var(--ds-dot-bullet-hover-background);
      --_focus-color: var(--ds-dot-bullet-focus-color);
      --_progress-indicator-color: var(--ds-dot-bullet-in-progress-indicator-color);
      --_progress-track-color: transparent;
      --_progress-track-border-color: var(--ds-dot-bullet-in-progress-outlined-track-color);
      --_progress-duration: var(--ds-dot-bullet-progress-duration-seconds);

      display: inline-flex;
      align-items: center;
      justify-content: center;
      inline-size: var(--ds-dot-bullet-size);
      block-size: var(--ds-dot-bullet-size);
      margin: 0;
      padding: 0;
      border: var(--ds-dot-bullet-focus-stroke-width) solid transparent;
      border-radius: var(--ds-dot-bullet-radius);
      background: transparent;
      color: var(--_default-color);
      box-sizing: border-box;
      cursor: pointer;
      appearance: none;
      outline: none;
    }

    .ds-dot-bullet--invert {
      --_default-color: var(--ds-dot-bullet-outlined-invert-color);
      --_selected-color: var(--ds-dot-bullet-filled-invert-selected-color);
      --_hover-background: var(--ds-dot-bullet-invert-hover-background);
      --_focus-color: var(--ds-dot-bullet-invert-focus-color);
      --_progress-indicator-color: var(--ds-dot-bullet-in-progress-invert-indicator-color);
      --_progress-track-border-color: var(--ds-dot-bullet-in-progress-outlined-invert-track-color);
    }

    .ds-dot-bullet--filled {
      --_default-color: var(--ds-dot-bullet-filled-default-color);
      --_selected-color: var(--ds-dot-bullet-filled-selected-color);
      --_progress-track-color: var(--ds-dot-bullet-in-progress-filled-track-color);
      --_progress-track-border-color: transparent;
    }

    .ds-dot-bullet--filled.ds-dot-bullet--invert {
      --_default-color: var(--ds-dot-bullet-filled-invert-default-color);
      --_selected-color: var(--ds-dot-bullet-filled-invert-selected-color);
      --_progress-track-color: var(--ds-dot-bullet-in-progress-filled-invert-track-color);
    }

    .ds-dot-bullet--hover:not(.ds-dot-bullet--selected):not(.ds-dot-bullet--in-progress),
    .ds-dot-bullet:hover:not(:disabled):not(.ds-dot-bullet--selected):not(.ds-dot-bullet--in-progress) {
      background: var(--_hover-background);
    }

    .ds-dot-bullet--focus:not(.ds-dot-bullet--selected):not(.ds-dot-bullet--in-progress),
    .ds-dot-bullet:focus-visible:not(.ds-dot-bullet--selected):not(.ds-dot-bullet--in-progress) {
      border-color: var(--_focus-color);
    }

    .ds-dot-bullet--in-progress {
      inline-size: var(--ds-dot-bullet-in-progress-width);
      padding-inline: var(--ds-dot-bullet-in-progress-padding-horizontal);
      border-width: 0;
    }

    .ds-dot-bullet__dot {
      display: block;
      inline-size: var(--ds-dot-bullet-dot-size);
      block-size: var(--ds-dot-bullet-dot-size);
      border: var(--ds-dot-bullet-dot-stroke-width) solid currentColor;
      border-radius: var(--ds-dot-bullet-radius);
      background: transparent;
      box-sizing: border-box;
      pointer-events: none;
    }

    .ds-dot-bullet--filled .ds-dot-bullet__dot {
      border-color: transparent;
      background: currentColor;
    }

    .ds-dot-bullet--selected {
      color: var(--_selected-color);
    }

    .ds-dot-bullet--selected .ds-dot-bullet__dot,
    .ds-dot-bullet:active:not(:disabled):not(.ds-dot-bullet--selected) .ds-dot-bullet__dot {
      border-color: currentColor;
      background: currentColor;
    }

    .ds-dot-bullet__progress-track {
      position: relative;
      display: block;
      inline-size: 100%;
      block-size: var(--ds-dot-bullet-in-progress-track-height);
      overflow: hidden;
      border: var(--ds-dot-bullet-dot-stroke-width) solid var(--_progress-track-border-color);
      border-radius: var(--ds-dot-bullet-radius);
      background: var(--_progress-track-color);
      box-sizing: border-box;
      pointer-events: none;
    }

    .ds-dot-bullet__progress-indicator {
      position: absolute;
      inset: 0 auto 0 0;
      display: block;
      inline-size: 100%;
      border-radius: var(--ds-dot-bullet-radius);
      background: var(--_progress-indicator-color);
      transform: scaleX(var(--_progress-start-scale, 0));
      transform-origin: left center;
      will-change: transform;
    }

    .ds-dot-bullet--in-progress:not(.ds-dot-bullet--progress-static)
      .ds-dot-bullet__progress-indicator {
      animation: ds-dot-bullet-fill var(--_progress-duration) linear forwards;
    }

    .ds-dot-bullet--progress-static .ds-dot-bullet__progress-indicator {
      transform: scaleX(1);
    }

    .ds-dot-bullet--in-progress.ds-dot-bullet--progress-paused
      .ds-dot-bullet__progress-indicator {
      animation-play-state: paused;
    }

    @keyframes ds-dot-bullet-fill {
      from { transform: scaleX(var(--_progress-start-scale, 0)); }
      to { transform: scaleX(1); }
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-dot-bullet__progress-indicator {
        transform: scaleX(1);
        animation: none !important;
      }
    }

    .ds-dot-bullet:disabled {
      cursor: default;
    }
  `],
})
export class DsDotBulletComponent implements OnChanges {
  @ViewChild('button', { static: true }) private buttonRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('progressIndicator') private progressIndicatorRef?: ElementRef<HTMLElement>;
  private styleValue: DsDotBulletStyle = 'outlined';

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  @Input() state: DsDotBulletState = 'default';
  @Input()
  set style(value: DsDotBulletStyle) {
    this.styleValue = value;
    this.changeDetectorRef.markForCheck();
  }

  get style(): DsDotBulletStyle {
    return this.styleValue;
  }
  @Input({ transform: booleanAttribute }) invert = false;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) ariaCurrent = false;
  @Input({ transform: numberAttribute }) tabIndex = 0;
  @Input() ariaLabel = '';
  @Input() progressDurationSeconds: number | string =
    'var(--ds-dot-bullet-progress-duration-seconds)';
  @Input({ transform: booleanAttribute }) progressPaused = false;
  @Input({ transform: numberAttribute }) progressStartPercent = 0;

  @Output() clicked = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    const progressPausedChange = changes['progressPaused'];
    if (
      this.state === 'in-progress'
      && progressPausedChange?.previousValue === true
      && !this.progressPaused
    ) {
      this.restartProgressAnimation();
    }
  }

  focus(): void {
    this.buttonRef?.nativeElement.focus();
  }

  private restartProgressAnimation(): void {
    const indicator = this.progressIndicatorRef?.nativeElement;
    if (!indicator) return;
    indicator.style.animation = 'none';
    indicator.getBoundingClientRect();
    indicator.style.animation = '';
  }

  get progressDurationCss(): string {
    const numericDuration = typeof this.progressDurationSeconds === 'number'
      ? this.progressDurationSeconds
      : Number(this.progressDurationSeconds);
    return Number.isFinite(numericDuration)
      ? `${Math.max(0, numericDuration)}s`
      : String(this.progressDurationSeconds);
  }

  get progressStartScale(): number {
    return Math.min(100, Math.max(0, this.progressStartPercent)) / 100;
  }

  get progressAnimationEnabled(): boolean {
    const numericDuration = typeof this.progressDurationSeconds === 'number'
      ? this.progressDurationSeconds
      : Number(this.progressDurationSeconds);
    return !Number.isFinite(numericDuration) || numericDuration > 0;
  }

  handleClick(): void {
    if (!this.disabled) this.clicked.emit();
  }
}
